import { loadStripePayments } from '../config/stripe';
import { auth } from '../config/firebase';
import { getFirestore, collection, doc, setDoc, getDoc, addDoc, onSnapshot, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getPricingPlans } from '../config/pricingPlans';
import { STRIPE_PRICE_IDS } from '../config/stripe';

// Initialize Stripe Payments
loadStripePayments();
const db = getFirestore();
const functions = getFunctions();

/**
 * Get all active products with prices from Firestore
 * @returns {Promise<Array>} Array of products with their active prices
 */
export const getProducts = async () => {
  try {
    // Get active products from Firestore
    const productsCollection = collection(db, 'products');
    const productsQuery = query(productsCollection, where('active', '==', true));
    const productsSnapshot = await getDocs(productsQuery);
    
    // Process each product and load its prices
    const productsPromises = productsSnapshot.docs.map(async (productDoc) => {
      const product = productDoc.data();
      product.id = productDoc.id;
      
      // Load active prices for this product
      const pricesCollection = collection(productDoc.ref, 'prices');
      const pricesQuery = query(pricesCollection, where('active', '==', true));
      const pricesSnapshot = await getDocs(pricesQuery);
      
      const prices = pricesSnapshot.docs.map(priceDoc => {
        const price = priceDoc.data();
        price.id = priceDoc.id;
        return price;
      });
      
      product.prices = prices;
      return product;
    });
    
    // Wait for all product promises to resolve
    const products = await Promise.all(productsPromises);
    console.log("Products loaded from Firestore:", products);
    return products;
  } catch (error) {
    console.error("Error fetching products from Firestore:", error);
    return [];
  }
};

/**
 * Get current user's latest subscription, regardless of status.
 * @returns {Promise<Object|null>} Latest subscription object or null if none exist.
 */
export const getSubscription = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    // Set timeout for Firestore operations to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Subscription fetch timeout')), 10000); // 10 second timeout
    });
    
    const fetchPromise = (async () => {
    // Check if customer document exists (optimization)
    try {
      const customerRef = doc(db, 'customers', user.uid);
      const customerDoc = await getDoc(customerRef);
      if (!customerDoc.exists()) {
        console.log("Customer document doesn't exist in Firestore.");
        return null;
      }
    } catch (customerError) {
      console.error("Error checking customer existence:", customerError);
      // Allow continuing to check subscriptions anyway
    }
    
    // Get all subscriptions for the user from Firestore
    const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
    const subscriptionsQuery = query(subscriptionsRef);
    const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
    
    if (subscriptionsSnapshot.empty) {
      console.log("No subscriptions found for user.");
      return null;
    }
    
    // Convert documents to array of objects and sort by creation date (latest first)
    const subscriptions = subscriptionsSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        const timeA = a.created?.seconds || 0;
        const timeB = b.created?.seconds || 0;
        return timeB - timeA; // Sort descending (latest first)
      });
      
    // Get the latest subscription
    const latestSubscription = subscriptions[0];
    console.log("Latest subscription found:", latestSubscription);

      // Only load product details if product exists and we need it
    if (latestSubscription && latestSubscription.product) {
      try {
          // Determine product ID format
        let productId;
        if (typeof latestSubscription.product === 'string') {
          productId = latestSubscription.product;
        } else if (latestSubscription.product.path) {
          productId = latestSubscription.product.id;
        } else {
           console.warn("Could not determine product ID from subscription data:", latestSubscription.product);
        }
        
        if (productId) {
            const productRef = doc(db, 'products', productId);
            const productSnap = await getDoc(productRef);
            if (productSnap.exists()) {
              latestSubscription.productDetails = { id: productSnap.id, ...productSnap.data() };
              console.log("Loaded product details for latest subscription:", latestSubscription.productDetails);
            }
         } else {
            console.log("No valid product reference found for the latest subscription.");
         }
      } catch (error) {
        console.error("Error fetching product details for latest subscription:", error);
          // Still return the subscription even if product details failed to load
      }
    }
    
    return latestSubscription || null;
    })();
    
    // Race between timeout and actual fetch
    return await Promise.race([fetchPromise, timeoutPromise]);
  } catch (error) {
    console.error("Error getting latest subscription:", error);
    if (error.message === 'Subscription fetch timeout') {
      console.warn("Subscription fetch timed out");
    } else if (error.message && error.message.includes("No such customer")) {
      console.warn("Customer ID not found in Stripe");
    }
    return null; // Return null on all errors
  }
};

/**
 * Check if user has an active subscription granting premium access.
 * Statuses considered active: 'active', 'trialing'.
 * @returns {Promise<boolean>} True if user has premium access.
 */
export const checkPremiumAccess = async () => {
  const subscription = await getSubscription(); // Gets the latest subscription
  // Check if the latest subscription exists and has a status that grants access
  return !!subscription && (subscription.status === 'active' || subscription.status === 'trialing');
};

/**
 * Create checkout session for subscription
 * @param {string} priceId - Stripe price ID
 * @param {string} successUrl - URL to redirect after success
 * @param {string} cancelUrl - URL to redirect after cancel
 * @returns {Promise<{sessionId: string}>} Session ID for checkout
 */
export const createCheckoutSession = async (priceId, successUrl, cancelUrl) => {
  if (!auth.currentUser) throw new Error("User must be logged in");

  if (!priceId) throw new Error('Missing price ID for checkout');

  console.log('==== CREATE CHECKOUT SESSION ====');
  console.log(`Price ID: ${priceId}`);
  console.log(`Success URL: ${successUrl}`);
  console.log(`Cancel URL: ${cancelUrl}`);
  console.log(`User: ${auth.currentUser.uid} (${auth.currentUser.email})`);
  console.log('===================================');

  const mode = priceId === STRIPE_PRICE_IDS.TRANSFER_ONETIME ? 'payment' : 'subscription';

  const createSession = httpsCallable(functions, 'createStripeCheckoutSession');
  const result = await createSession({
    priceId,
    successUrl: successUrl || window.location.origin,
    cancelUrl: cancelUrl || window.location.origin,
    mode,
  });

  const { url, sessionId } = result.data;
  console.log('Got checkout session from Cloud Function:', { sessionId, url });
  return { url, sessionId };
};

/**
 * Create a customer portal session using the Firebase Extension's Cloud Function.
 * @param {string} returnUrl - URL to redirect to after the portal session.
 * @returns {Promise<{url: string}>} Portal URL.
 */
export const createPortalSession = async (returnUrl) => {
  if (!auth.currentUser) throw new Error("User must be logged in");

  console.log('Calling createPortalLink Cloud Function...');
  
  try {
    // Prepare the function call
    // Ensure the function name matches your extension instance ID if customized
    const functionRef = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
    
    // Call the function with the returnUrl
    const result = await functionRef({
      returnUrl: returnUrl || window.location.origin,
    });

    // Check if the result has the expected URL
    if (result.data && result.data.url) {
      console.log('Successfully received portal URL:', result.data.url);
      return { url: result.data.url };
    } else {
      console.error('Cloud function result missing URL:', result.data);
      throw new Error('Failed to retrieve portal URL from Cloud Function.');
    }
  } catch (error) {
    console.error("Error calling createPortalLink function:", error);
    // Handle specific HTTPS errors if needed
    if (error.code === 'functions/not-found') {
      throw new Error('Could not find the createPortalLink Cloud Function. Check extension installation and function name.');
    } else if (error.code === 'functions/permission-denied') {
      throw new Error('Permission denied when calling createPortalLink. Ensure user is authenticated.');
    } else {
      // Rethrow other errors
      throw new Error(`Failed to create portal session: ${error.message}`);
    }
  }
};

/**
 * Check if product is in user's subscription
 * @param {string} productId - Stripe product ID
 * @returns {Promise<boolean>} True if product is in subscription
 */
export const isProductInSubscription = async (productId) => {
  const subscription = await getSubscription();
  if (!subscription) return false;
  
  return subscription.product.id === productId;
};

/**
 * Create a manual checkout session without using an existing price
 * @param {number} amount - Amount in the smallest currency unit (e.g., cents for USD)
 * @param {string} currency - Currency code (e.g., 'usd')
 * @param {string} successUrl - URL to redirect after success
 * @param {string} cancelUrl - URL to redirect after cancel
 * @param {string} [interval='month'] - Billing interval (month or year)
 * @returns {Promise<{sessionId: string}>} Session ID for checkout
 */
export const createManualCheckoutSession = async (amount, currency, successUrl, cancelUrl, interval = 'month') => {
  try {
    if (!auth.currentUser) throw new Error("User must be logged in");

    console.log('==== CREATE MANUAL CHECKOUT SESSION ====');
    console.log(`Amount: ${amount/100} ${currency}, Interval: ${interval}`);
    console.log(`Success URL: ${successUrl}`);
    console.log(`Cancel URL: ${cancelUrl}`);
    console.log(`User: ${auth.currentUser.uid} (${auth.currentUser.email})`);
    console.log('=======================================');

    // Verify all required parameters are present
    if (!amount || !currency || !successUrl || !cancelUrl) {
      console.error('Missing required parameters for manual checkout session');
      throw new Error('Missing required parameters for checkout');
    }

    // Check if customer exists
    try {
      const customerRef = doc(db, 'customers', auth.currentUser.uid);
      const customerDoc = await getDoc(customerRef);
      
      // If customer doesn't exist, add document so Firebase extension can create customer in Stripe
      if (!customerDoc.exists()) {
        console.log("Customer document doesn't exist in Firestore, creating it first");
        await setDoc(customerRef, {
          app_metadata: { provider: "firebase" },
          email: auth.currentUser.email,
          user_id: auth.currentUser.uid,
          created_at: new Date().toISOString()
        });
      }
    } catch (customerError) {
      console.error("Error checking/creating customer:", customerError);
      // Continue anyway - the extension will handle customer creation if needed
    }

    // Create new session in Firestore
    const checkoutSessionRef = collection(db, 'customers', auth.currentUser.uid, 'checkout_sessions');
    console.log('Created reference to checkout_sessions collection');
    
    // Manual session data structure
    const sessionData = {
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: 'subscription',
      
      // Manual price data structure
      line_items: [{
        quantity: 1,
        price_data: {
          currency,
          unit_amount: amount,
          product_data: {
            name: 'Pro Subscription',
            description: 'Monthly subscription to Pro plan',
          },
          recurring: {
            interval: interval === 'year' ? 'year' : 'month',
          },
        },
      }],
      
      // Additional data that might be useful
      metadata: {
        userId: auth.currentUser.uid,
        planType: 'pro',
        billingPeriod: interval,
        amount: amount,
        currency: currency,
      },
    };
    
    console.log('Session data prepared:', JSON.stringify(sessionData, null, 2));
    
    try {
      // Create new document with session data for manual checkout
      console.log('Adding document to checkout_sessions collection...');
      const docRef = await addDoc(checkoutSessionRef, sessionData);
      console.log('Document added successfully, ID:', docRef.id);
      
      // Listen for changes to get sessionId from extension
      console.log('Setting up listener for session ID...');
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe();
          console.error('Timeout waiting for session ID');
          reject(new Error('Timeout waiting for session ID'));
        }, 30000); // 30 seconds timeout
        
        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data() || {};
          console.log('Session document updated:', data);
          
          const { error, sessionId } = data;
          
          if (error) {
            clearTimeout(timeout);
            unsubscribe();
            console.error('Stripe error:', error);
            
            // Special handling for "No such customer" error
            if (error.message && error.message.includes("No such customer")) {
              reject(new Error(`Stripe error: There's an issue with your customer account. Please try again or contact support.`));
            } else {
              reject(new Error(`Stripe error: ${error.message}`));
            }
          }
          
          if (sessionId) {
            clearTimeout(timeout);
            console.log('Got session ID from extension for manual checkout:', sessionId);
            unsubscribe();
            resolve({ sessionId });
          }
        });
      });
    } catch (innerError) {
      console.error('Error in Firestore operations:', innerError);
      throw innerError;
    }
  } catch (error) {
    console.error("Error creating manual checkout session:", error);
    throw error;
  }
};

/**
 * Get all prices from Firestore without filters
 * @returns {Promise<Array>} Array of all prices
 */
export const getAllPrices = async () => {
  try {
    console.log('Fetching all Stripe prices from Firestore...');
    const products = [];
    
    // Get all products
    const productsCollection = collection(db, 'products');
    const productsSnapshot = await getDocs(productsCollection);
    
    if (productsSnapshot.empty) {
      console.log('No products found in Firestore');
      return [];
    }
    
    console.log(`Found ${productsSnapshot.docs.length} products`);
    
    // Array of promises for loading prices for each product
    const productsPromises = productsSnapshot.docs.map(async (productDoc) => {
      const product = productDoc.data();
      product.id = productDoc.id;
      product.path = productDoc.ref.path;
      
      // Load all prices for the product without filter
      const pricesCollection = collection(productDoc.ref, 'prices');
      const pricesSnapshot = await getDocs(pricesCollection);
      
      const prices = pricesSnapshot.docs.map(priceDoc => {
        const price = priceDoc.data();
        price.id = priceDoc.id;
        price.path = priceDoc.ref.path;
        return price;
      });
      
      console.log(`Product ${product.id} (${product.name || 'no name'}) has ${prices.length} prices`);
      product.prices = prices;
      
      products.push(product);
    });
    
    // Wait for all promises to resolve
    await Promise.all(productsPromises);
    
    // Print all prices in readable format
    products.forEach(product => {
      console.log(`\nProduct: ${product.id} (${product.name || 'no name'})`);
      console.log(`Active: ${product.active}`);
      console.log(`Path: ${product.path}`);
      
      if (product.prices && product.prices.length > 0) {
        console.log('Prices:');
        product.prices.forEach(price => {
          console.log(`  - ID: ${price.id}`);
          console.log(`    Path: ${price.path}`);
          console.log(`    Active: ${price.active}`);
          console.log(`    Currency: ${price.currency}`);
          console.log(`    Unit amount: ${price.unit_amount / 100} ${price.currency}`);
          if (price.recurring) {
            console.log(`    Interval: ${price.recurring.interval}`);
          } else {
            console.log(`    One-time payment`);
          }
          console.log(`    Created: ${new Date(price.created * 1000).toLocaleString()}`);
        });
      } else {
        console.log('No prices for this product');
      }
    });
    
    return products;
  } catch (error) {
    console.error('Error fetching all prices:', error);
    return [];
  }
};

/**
 * Reset customer data in Firestore
 * This can be used when there's a problem with customer data synchronization
 * @returns {Promise<boolean>} True if reset was successful
 */
export const resetCustomer = async () => {
  try {
    if (!auth.currentUser) {
      console.error("User must be logged in to reset customer data");
      return false;
    }

    console.log('==== RESET CUSTOMER DATA ====');
    console.log(`User: ${auth.currentUser.uid} (${auth.currentUser.email})`);
    console.log('===========================');

    // Our "key": delete the customer and recreate it in Firestore
    // This will cause the Firebase extension to recreate the customer in Stripe

    // Check if customers collection exists
    const customerRef = doc(db, 'customers', auth.currentUser.uid);
    const customerDoc = await getDoc(customerRef);

    // If customer exists, delete it
    if (customerDoc.exists()) {
      console.log("Deleting existing customer document");
      
      // Optional: backup customer data before deletion
      const customerData = customerDoc.data();
      const backupRef = doc(db, 'customer_backups', auth.currentUser.uid);
      await setDoc(backupRef, {
        ...customerData,
        backed_up_at: new Date().toISOString(),
        original_path: `customers/${auth.currentUser.uid}`
      });
      
      // Delete the customer document
      // Instead of deleting the document, we mark it as inactive
      await setDoc(customerRef, {
        active: false,
        marked_for_reset: true,
        reset_at: new Date().toISOString(),
        original_data: customerData
      });
    }

    // Create new customer document
    console.log("Creating new customer document");
    await setDoc(customerRef, {
      app_metadata: { provider: "firebase" },
      email: auth.currentUser.email,
      user_id: auth.currentUser.uid,
      created_at: new Date().toISOString(),
      active: true
    });

    console.log("Customer data reset complete");
    return true;
  } catch (error) {
    console.error("Error resetting customer data:", error);
    return false;
  }
};

/**
 * Get mapped plans from Stripe products
 * This function maps Stripe products to our pricing plan structure
 * @returns {Promise<Object>} Mapped pricing plans
 */
export const getStripePlans = async (selectedBillingPeriod = 'monthly') => {
  try {
    console.log('Getting Stripe plans for billing period:', selectedBillingPeriod);
    const products = await getProducts();
    
    if (!products || products.length === 0) {
      console.log('No products found in Stripe');
      return null;
    }
    
    // Define our plan types that come from Stripe
    const planTypes = ['pro', 'business'];
    
    // Get default plans from config/pricingPlans.js
    // So we can use the existing structure instead of duplicating code
    const defaultPlans = getPricingPlans(selectedBillingPeriod);
    
    // Copy plans to avoid modifying the original
    const plans = JSON.parse(JSON.stringify(defaultPlans));
    
    // Map products to our plan structure
    planTypes.forEach(planType => {
      // Find product by name (case insensitive)
      const product = products.find(p => 
        p.name && p.name.toLowerCase() === planType.toLowerCase()
      );
      
      if (!product) {
        console.log(`No Stripe product found for plan type: ${planType}`);
        // In this case, the plan already exists in the default structure, so no need to do anything
        return;
      }
      
      console.log(`Found Stripe product for ${planType}:`, product.id, product.name);
      
      // Use product properties, if they exist
      if (product.metadata && product.metadata.features) {
        plans[planType].features = product.metadata.features
          .split(',')
          .map(feature => feature.trim());
      }
      
      // Find price for the selected billing period
      let price = null;
      let period = null;
      let stripePriceId = null;
      
      if (product.prices && product.prices.length > 0) {
        // Find matching price for this billing period
        const matchingPrice = product.prices.find(p => 
          p.active && 
          p.recurring &&
          ((selectedBillingPeriod === 'monthly' && p.recurring.interval === 'month') ||
           (selectedBillingPeriod === 'year' && p.recurring.interval === 'year'))
        );
        
        if (matchingPrice) {
          if (selectedBillingPeriod === 'monthly') {
            // For monthly plan - show monthly price as usual
            price = (matchingPrice.unit_amount / 100).toString();
            period = planType === 'business' ? '/user/month' : '/month';
          } else {
            // For yearly plan - calculate monthly price
            const yearlyAmount = matchingPrice.unit_amount / 100;
            const monthlyAmount = Math.round((yearlyAmount / 12) * 10) / 10; // Round to one decimal place
            price = monthlyAmount.toString();
            period = planType === 'business' ? '/user/month, billed yearly' : '/month, billed yearly';
          }
          
          stripePriceId = matchingPrice.id;
          console.log(`Found price for ${planType} ${selectedBillingPeriod}:`, stripePriceId, `${price} ${matchingPrice.currency}`);
          
          // Update price details
          plans[planType].price = price;
          plans[planType].period = period;
          plans[planType].stripePriceId = stripePriceId;
        }
      }
      
      // Add product ID in any case
      plans[planType].stripeProductId = product.id;
    });
    
    console.log('Final mapped plans:', plans);
    return plans;
  } catch (error) {
    console.error('Error mapping Stripe plans:', error);
    return null;
  }
}; 